import { XAxisOptions, YAxisOptions } from "highcharts";
import { GroupedSheetConfig, TITLE_TYPE, TitleSchema } from "./dynamic-data-table.model";
export const pointFormat = `<span style="color:{point.color}">{point.name}</span>: <b>{point.y}<br/>`
export const headerFormat = `<span style="font-size:11px">{point.name}</span><br>`;

export const CHART_TYPE = {
    ColumnChart: 'column',
    PieChart: 'pie',
    DonutChart: 'pie',
    BarChart: 'bar',
    BubbleChart: 'bubble',
    AreasplineChart: 'areaspline'
} as const

export const AxisType = {
  Category: 'category'
} as const

export type ChartTypes = typeof CHART_TYPE[keyof typeof CHART_TYPE];

export type Series = {
    name?:any,
    key?:any,
    type?: ChartTypes,
    color?: string,
    data: Array<any>,
    dataLabels?: {
      enabled: boolean,
      format?: string,
      align?: string,
      inside?:boolean,
      style?:any
      x?:any,
      y?:any
    },
}

export type TooltipConfig = {
    headerFormat?: string,
    pointFormat?: string,
    shared?: boolean,
    useHTML?: boolean,
    formatter?: Function
}

export type chartHeaderInfo = {
  value: any,
  label: string,
  style?: any
}

export type DrilldownToolip = {
  fieldsInfo:Array<{fieldName:string,displayName:string}>
}
export type DrillDownConfig = {
  label:string,
  value:string,
  type:ChartTypes, 
  drillDownField:string,
  xAxis?:any,
  yAxis?:any,
  titleSchema?:Record<string,TitleSchema> | TitleSchema,
  tooltip?:TooltipConfig,
  dataLabels?: any,
  drillDownConfig?: DrillDownConfig,
}

export type ChartDataSchema = {
  label:string,
  tooltipLabelInfo?:any,
  value:any,
  drillDownConfig?:DrillDownConfig,
}

export type MapDrilldownTableColumn = {
  label: string,
  fieldKey: string,
  valueType?: string,
  className?: string,
}

export type MapDrilldownConfig = {
  tableColumns: Array<MapDrilldownTableColumn>,
  areaValueFieldKey: string,
  stateValueFieldKey: string,
  sortFieldKey: string,
  areaMetricLabel: string,
  stateMetricLabel: string,
  footerMetricLabel: string,
  footerMetricFieldKey: string,
  valueType?: string,
}
 

export interface CommonChartModel {
    dataSchema?:ChartDataSchema,
    plotOptions?:any,
    legend?:any,
    type: ChartTypes,
    isPie?:boolean,
    isDonut?:boolean,
    chartRef?: any,
    subtitle?:string,
    title?:string,
    titleSchema: TitleSchema | Record<string,TitleSchema>,
    xAxis?: XAxisOptions,
    yAxis?: YAxisOptions,
    series?: Array<Series>,
    tooltip?: TooltipConfig,
    drilldown?: any,
    data?: Array<any>,
    dataLabels?:any,
    mapDrilldownConfig?: MapDrilldownConfig,
    exportConfig:{
      fileName: string,
      schema?:any,
      groupedSheets?: GroupedSheetConfig
    },
    isLineChart?: boolean,
}



export type DataConfig = {
  data: Array<any>,
  drilldown?: Array<any>,
  categories?: Array<any>
}

export const ColumnChartModel: CommonChartModel= {
    type: CHART_TYPE['ColumnChart'],
    titleSchema: {
      label:'',
      title:'Column Chart Title',
      type:TITLE_TYPE['Static']
    },
    subtitle: '',
    xAxis: {
      type: AxisType['Category'],
      title: {
        text: 'X-Axis',
      }
    },
    yAxis: {
      title: {
        text: 'Y-Axis',
      }
    },
    series: [
      {
        color: '#7ab1e7',
        data: [],
        dataLabels: {
          enabled: true
        },
      },
    ],
    tooltip: {
      headerFormat: headerFormat,
      pointFormat:pointFormat
    },
    drilldown: {
        breadcrumbs: {
            position: {
              align: 'right'
            }
        },
        series: []
    },
    exportConfig:{
      fileName:'ExportColumnChart'
    }
}

export const PieChartModel: CommonChartModel = {
    type: CHART_TYPE['PieChart'],
    isPie:true,
    titleSchema: {
      label:'',
      title:'Pie Chart Title',
      type:TITLE_TYPE['Static']
    },
    data: [],
    series: [    {
      type: CHART_TYPE['PieChart'],
      data: []
    }],
    tooltip: {
        headerFormat: headerFormat,
        pointFormat:pointFormat
    },
    drilldown: {
        breadcrumbs: {
          position: {
            align: 'right'
          }
        },
      series: []
      },
      exportConfig:{
        fileName:'ExportPieChart'
      }
}

export const DonutChartModel: CommonChartModel = {
    type: CHART_TYPE['DonutChart'],
    isPie: true,
    isDonut: true,
    titleSchema: {
      label: '',
      title: 'Donut Chart Title',
      type: TITLE_TYPE['Static']
    },
    data: [],
    series: [{
      type: CHART_TYPE['DonutChart'],
      data: []
    }],
    plotOptions: {
      pie: {
        innerSize: '70%',
        allowPointSelect: true,
        cursor: 'pointer',
        showInLegend: true,
        dataLabels: { enabled: true }
      }
    },
    tooltip: {
        headerFormat: headerFormat,
        pointFormat: pointFormat
    },
    drilldown: {
        breadcrumbs: { position: { align: 'right' } },
        series: []
    },
    exportConfig: {
      fileName: 'ExportDonutChart'
    }
}

export const BarChartModel: CommonChartModel = {
  type: CHART_TYPE['BarChart'],
  titleSchema: {
    label:'',
    title:'Bar Chart Title',
    type:TITLE_TYPE['Static']
  },
  xAxis: {
    labels: {
      style: {
        fontSize: '8px',
      },
    },
    title: {
      text: 'X-Axis',
    },
    categories:[]
  },
  yAxis: {
    title: {
      text: 'Y-Axis',
    },
  },
  series: [
    {
      name: '',
      type: CHART_TYPE['BarChart'],
      color: '#7ab1e7',
      data: [],
      dataLabels: {
        enabled: false,
      },
    },
  ],
    exportConfig:{
      fileName:'ExportBarChart'
    }
}

export const BubbleChartModel: CommonChartModel = {
  type: CHART_TYPE['BubbleChart'],
  titleSchema: {
    label:'',
    title:'Bubble Chart Title',
    type:TITLE_TYPE['Static']
  },
  xAxis: {
    title: {
      text: 'Dealer Index',
    },
    type: 'linear',
    min: 0,
  },
  yAxis: {
    title: {
      text: 'Total Sales Units',
    },
    startOnTick: true,
    endOnTick: true,
  },
  tooltip: {
    headerFormat: '<b>{series.name}</b><br>',
    pointFormat: '{point.name}: {point.y} units, Bubble size: {point.z}'
  },
  series: [
    {
      name: 'Dealer Sales',
      type: CHART_TYPE['BubbleChart'],
      color: '#FF6B6B',
      data: [],
      dataLabels: {
        enabled: false,
      },
    },
  ],
  plotOptions: {
    bubble: {
      minSize: 8,
      maxSize: 30,
    }
  },
  exportConfig:{
    fileName:'ExportBubbleChart'
  }
}

export const AreasplineChartModel: CommonChartModel = {
  type: CHART_TYPE['AreasplineChart'],
  titleSchema: {
    label:'',
    title:'Areaspline Chart Title',
    type:TITLE_TYPE['Static']
  },
  xAxis: {
    title: {
      text: 'Dealer',
    },
    type: 'category',
  },
  yAxis: {
    title: {
      text: 'Sales Volume',
    },
    min: 0,
  },
  tooltip: {
    headerFormat: '<b>{point.x}</b><br>',
    pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> units<br/>'
  },
  series: [
    {
      name: 'Sales Trend',
      type: CHART_TYPE['AreasplineChart'],
      color: '#4ECDC4',
      data: [],
      dataLabels: {
        enabled: false,
      },
    },
  ],
  plotOptions: {
    areaspline: {
      fillOpacity: 0.4,
    }
  },
  exportConfig:{
    fileName:'ExportAreasplineChart'
  }
}
